import type { HostOptions } from "@algolia/transporter"

import { logger } from "@/modules/monitoring/logger"

type GetSearchProxyHostsOptions = {
  isDirectAlgoliaConnectionEnabled?: boolean
  isSearchProxyV3ConnectionEnabled?: boolean
  isMultiHostConnectionEnabled?: boolean
}

const getSearchProxyProtocol = (): "http" | "https" => {
  if (
    (typeof window === "undefined" && process.env.NEXT_PUBLIC_ENV !== "local") ||
    process.env.NEXT_PUBLIC_MOCK_ENV_CONFIG === "true"
  ) {
    return "http"
  }

  return "https"
}

const getAlgoliaSearchProxyHost = (protocol: string): HostOptions => {
  if (typeof window === "undefined" && process.env.NEXT_PUBLIC_ENV !== "local") {
    const internalSearchProxyHost = process.env.INTERNAL_ALGOLIA_CUSTOM_HOST_URL
    if (internalSearchProxyHost !== undefined) {
      return {
        protocol,
        url: internalSearchProxyHost,
      }
    }
    logger.error("INTERNAL_ALGOLIA_CUSTOM_HOST_URL is not defined, defaulting to external host")
  }

  return {
    protocol,
    url: process.env.NEXT_PUBLIC_ALGOLIA_CUSTOM_HOST_URL,
  }
}

const getAlgoliaSearchProxyV3Host = (protocol: string): HostOptions => {
  if (typeof window === "undefined" && process.env.NEXT_PUBLIC_ENV !== "local") {
    const internalSearchProxyV3Host = process.env.INTERNAL_SEARCH_PROXY_V3_HOST
    if (internalSearchProxyV3Host !== undefined) {
      return {
        protocol,
        url: internalSearchProxyV3Host,
      }
    }
    logger.error("INTERNAL_SEARCH_PROXY_V3_HOST is not defined, defaulting to external host")
  }

  return {
    protocol,
    url: process.env.NEXT_PUBLIC_SEARCH_PROXY_V3_HOST,
  }
}

const getAlgoliaSearchProxyMultiHosts = (protocol: string): readonly HostOptions[] => {
  return [
    { protocol, url: process.env.NEXT_PUBLIC_SEARCH_PROXY_MULTI_HOST_DSN },
    { protocol, url: process.env.NEXT_PUBLIC_SEARCH_PROXY_MULTI_HOST_1 },
    { protocol, url: process.env.NEXT_PUBLIC_SEARCH_PROXY_MULTI_HOST_2 },
    { protocol, url: process.env.NEXT_PUBLIC_SEARCH_PROXY_MULTI_HOST_3 },
  ]
}

export const getSearchProxyHosts = (options: GetSearchProxyHostsOptions): readonly HostOptions[] | undefined => {
  const searchProxyProtocol = getSearchProxyProtocol()

  if (options.isDirectAlgoliaConnectionEnabled && typeof window === "undefined") {
    return undefined
  }

  if (options.isMultiHostConnectionEnabled && typeof window !== "undefined") {
    return getAlgoliaSearchProxyMultiHosts(searchProxyProtocol)
  }

  if (options.isSearchProxyV3ConnectionEnabled) {
    return [getAlgoliaSearchProxyV3Host(searchProxyProtocol)]
  }

  return [getAlgoliaSearchProxyHost(searchProxyProtocol)]
}
